import { Button } from "antd";
import moment from "moment";
import React, { forwardRef, Fragment, useEffect, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import getSetting, { getLogoUrl } from "../../api/getSettings";

const DebitNoteBody = forwardRef(({ data, supplier, invoiceData }, ref) => {
  const totalQty = data?.returnPurchaseInvoiceProduct?.reduce(
    (sum, item) => sum + (item.product_quantity || 0),
    0
  );
  const totalAmount = data?.returnPurchaseInvoiceProduct?.reduce(
    (sum, item) => {
      const qty = Number(item.product_quantity || 0);
      if (qty <= 0) return sum;
      const gross = qty * Number(item.product_purchase_price || 0);
      const discountPct = Number(
        item.product_purchase_discount ?? item.product_discount ?? 0
      );
      const discountAmt = (gross * discountPct) / 100;
      return sum + (gross - discountAmt);
    },
    0
  );

  return (
    <Fragment>
      <div ref={ref} style={{ padding: 16, fontFamily: 'Arial, sans-serif' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ margin: 0 }}>Debit Note</h2>
            <div style={{ fontSize: 12 }}>No: {data?.id}</div>
            <div style={{ fontSize: 12 }}>Date: {moment(data?.date).format('DD-MMM-YYYY')}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            {invoiceData?.logo && (
              <div style={{ marginBottom: 8 }}>
                <img src={getLogoUrl(invoiceData.logo)} alt="Company Logo" style={{ maxHeight: '50px', width: 'auto' }} />
              </div>
            )}
            <div style={{ fontWeight: 'bold' }}>{invoiceData?.company_name}</div>
            <div style={{ fontSize: 12 }}>{invoiceData?.address}</div>
            <div style={{ fontSize: 12 }}>Phone: {invoiceData?.phone}</div>
            <div style={{ fontSize: 12 }}>Email: {invoiceData?.email}</div>
          </div>
        </div>

        <hr />

        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 12 }}>To,</div>
            <div style={{ fontWeight: 'bold' }}>{supplier?.name || 'Supplier'}</div>
            {supplier?.address && (
              <div style={{ fontSize: 12 }}>{supplier.address}</div>
            )}
            {supplier?.phone && (
              <div style={{ fontSize: 12 }}>Phone: {supplier.phone}</div>
            )}
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 12 }}>Purchase Ref: #{data?.purchaseInvoice_id}</div>
          </div>
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr>
              <th style={{ border: '1px solid #000', padding: 6, textAlign: 'left' }}>#</th>
              <th style={{ border: '1px solid #000', padding: 6, textAlign: 'left' }}>Item</th>
              <th style={{ border: '1px solid #000', padding: 6, textAlign: 'right' }}>Price</th>
              <th style={{ border: '1px solid #000', padding: 6, textAlign: 'right' }}>Qty</th>
              <th style={{ border: '1px solid #000', padding: 6, textAlign: 'right' }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {data?.returnPurchaseInvoiceProduct?.filter((p) => Number(p.product_quantity || 0) > 0).map((p, idx) => {
              const gross = Number(p.product_quantity || 0) * Number(p.product_purchase_price || 0);
              const discountPct = Number(p.product_purchase_discount ?? p.product_discount ?? 0);
              const discountAmt = (gross * discountPct) / 100;
              const lineAmount = gross - discountAmt;
              return (
                <tr key={p.id || idx}>
                  <td style={{ border: '1px solid #000', padding: 6 }}>{idx + 1}</td>
                  <td style={{ border: '1px solid #000', padding: 6 }}>
                    {p.product?.name || '-'}
                  </td>
                  <td style={{ border: '1px solid #000', padding: 6, textAlign: 'right' }}>
                    {p.product_purchase_price?.toFixed ? p.product_purchase_price.toFixed(2) : p.product_purchase_price}
                  </td>
                  <td style={{ border: '1px solid #000', padding: 6, textAlign: 'right' }}>
                    {p.product_quantity}
                    {discountPct ? ` (disc ${discountPct}%)` : ''}
                  </td>
                  <td style={{ border: '1px solid #000', padding: 6, textAlign: 'right' }}>
                    {lineAmount.toFixed(2)}
                  </td>
                </tr>
              );
            })}
            <tr>
              <td style={{ border: '1px solid #000', padding: 6 }} colSpan={3}><b>Total</b></td>
              <td style={{ border: '1px solid #000', padding: 6, textAlign: 'right' }}><b>{totalQty}</b></td>
              <td style={{ border: '1px solid #000', padding: 6, textAlign: 'right' }}><b>{(totalAmount || 0).toFixed(2)}</b></td>
            </tr>
          </tbody>
        </table>

        {data?.note && (
          <div style={{ marginTop: 12, fontSize: 12 }}>
            <b>Note:</b> {data.note}
          </div>
        )}

        <div style={{ marginTop: 24, display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 12 }}>Authorized Signatory</div>
          <div style={{ fontSize: 12 }}>Received By</div>
        </div>
      </div>
    </Fragment>
  );
});

const DebitNote = ({ data, supplier }) => {
  const componentRef = useRef();
  const handlePrint = useReactToPrint({ content: () => componentRef.current });
  const [invoiceData, setInvoiceData] = useState(null);

  useEffect(() => {
    getSetting().then((res) => setInvoiceData(res?.result));
  }, []);

  if (!invoiceData) return null;

  return (
    <div>
      <div className="hidden">
        <DebitNoteBody ref={componentRef} data={data} supplier={supplier} invoiceData={invoiceData} />
      </div>
      <Button type="primary" shape="round" onClick={handlePrint}>
        Print Debit Note
      </Button>
    </div>
  );
};

export default DebitNote;


